import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import axios from "axios";
import { postWithRetry } from "../lib/apiResilient";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Check,
  Lock,
  Download,
  Sparkles,
  Mail,
  CreditCard,
  ShieldCheck,
  Heart,
  X,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import { Toaster } from "../components/ui/sonner";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function formatPrice(p) {
  if (!p) return "Free";
  return `$${Number(p).toLocaleString()}`;
}

// Legacy id formats (f01, p03, x02, etc.) — bounce back to the Studio index.
// Those pages are retired (the assets they advertised never shipped).
const LEGACY_ID_RE = /^[a-z]\d{2}$/i;

export default function StudioProduct() {
  const { id } = useParams();
  const navigate = useNavigate();

  if (LEGACY_ID_RE.test(id || "")) {
    return <Navigate to="/studio" replace />;
  }

  return <StudioProductImpl slug={id} navigate={navigate} />;
}

function StudioProductImpl({ slug, navigate }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showBuy, setShowBuy] = useState(false);
  const [showFreeGate, setShowFreeGate] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/studio/product/${slug}`)
      .then(({ data }) => setProduct(data))
      .catch(() => setError("This edition isn't available."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="bg-cream min-h-screen pt-40 px-6 text-center">
        <p className="text-ink/60 font-serif italic">Loading…</p>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="bg-cream min-h-screen pt-40 px-6 text-center">
        <p className="text-ink/60 font-serif italic mb-6">{error || "Not found"}</p>
        <Link to="/studio" className="text-xs tracking-[0.25em] uppercase underline">
          Back to the Studio
        </Link>
      </div>
    );
  }

  const isFree = product.is_free;
  const isCoaching = product.no_download;

  return (
    <div className="bg-cream min-h-screen text-ink">
      <Toaster />
      {/* Top bar */}
      <div className="border-b border-ink/10 pt-24 pb-4 bg-cream sticky top-0 z-40 backdrop-blur">
        <div className="max-w-screen-xl mx-auto px-6 sm:px-10 flex items-center justify-between">
          <Link
            to="/studio"
            data-testid="back-to-studio"
            className="inline-flex items-center gap-2 text-xs tracking-[0.25em] uppercase text-ink/70 hover:text-ink"
          >
            <ArrowLeft className="w-3.5 h-3.5"/>
            Back to the Studio
          </Link>
          <p className="text-[10px] tracking-[0.3em] uppercase text-ink/50">
            No. {product.edition_no} · {product.tier}
          </p>
        </div>
      </div>

      {/* HERO */}
      <section className="py-16 sm:py-20 border-b border-ink/10">
        <div className="max-w-screen-xl mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Cover */}
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative aspect-[4/5] bg-ink overflow-hidden">
                <img
                  src={`${API}${product.cover_url}`}
                  alt={product.title}
                  onError={(e) => { e.target.style.display = "none"; }}
                  className="w-full h-full object-cover"
                />
                {isCoaching && (
                  <div className="absolute inset-0 bg-gradient-to-br from-ink to-ink/80 grid place-items-center">
                    <div className="text-center text-cream p-10">
                      <Sparkles className="w-10 h-10 mx-auto opacity-70 mb-4" strokeWidth={1}/>
                      <p className="font-display text-3xl leading-tight">{product.title}</p>
                    </div>
                  </div>
                )}
              </div>
              {/* Edition seal under cover */}
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="border border-ink/15 p-3 bg-white">
                  <p className="text-[8px] tracking-[0.25em] uppercase text-ink/50">Edition</p>
                  <p className="font-display text-lg text-ink">No. {product.edition_no}</p>
                </div>
                <div className="border border-ink/15 p-3 bg-white">
                  <p className="text-[8px] tracking-[0.25em] uppercase text-ink/50">Tier</p>
                  <p className="font-serif italic text-sm text-ink">{product.tier}</p>
                </div>
                <div className="border border-ink/15 p-3 bg-white">
                  <p className="text-[8px] tracking-[0.25em] uppercase text-ink/50">Year</p>
                  <p className="font-serif italic text-sm text-ink">2026</p>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Info */}
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-4">
                {product.category_label}
              </p>
              <h1 className="font-display text-4xl sm:text-6xl lg:text-7xl leading-[0.95] mb-6">
                {product.title}
              </h1>
              <p className="font-serif italic text-xl sm:text-2xl text-ink/70 leading-snug mb-10 max-w-2xl">
                {product.blurb}
              </p>
            </Reveal>

            {/* Price + CTA */}
            <Reveal delay={0.1}>
              <div className="border border-ink/15 bg-white p-6 sm:p-8 mb-6">
                <div className="flex items-baseline justify-between mb-6">
                  <div>
                    <p className="text-[10px] tracking-[0.3em] uppercase text-ink/50 mb-1">
                      {isFree ? "Free · Instant download" : "One-time"}
                    </p>
                    <p className="font-display text-5xl text-ink">{formatPrice(product.price)}</p>
                  </div>
                  {!isCoaching && product.bundle_size_mb > 0 && (
                    <p className="text-xs text-ink/50 tracking-[0.2em] uppercase">
                      Bundle · {product.bundle_size_mb}MB
                    </p>
                  )}
                </div>

                {isFree ? (
                  <button
                    type="button"
                    data-testid="cta-get-free"
                    onClick={() => setShowFreeGate(true)}
                    className="w-full inline-flex items-center justify-center gap-3 bg-ink text-cream py-4 text-xs tracking-[0.25em] uppercase hover:bg-ink/80 transition-colors"
                  >
                    <Download className="w-4 h-4" strokeWidth={1.5}/>
                    Get instant access
                  </button>
                ) : isCoaching ? (
                  <>
                    <div className="bg-ink/5 border border-ink/15 p-4 mb-3 text-left" data-testid="coaching-inclusion-banner">
                      <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-1">Includes — limited offer</p>
                      <p className="font-serif italic text-ink text-base leading-snug">Every paid edition in the Studio · <span className="font-medium">$2,722 value</span> · yours instantly when payment clears.</p>
                    </div>
                    <Link
                      to="/studio/coaching/apply"
                      data-testid="cta-coaching-apply"
                      className="w-full inline-flex items-center justify-center gap-3 bg-ink text-cream py-4 text-xs tracking-[0.25em] uppercase hover:bg-ink/80 transition-colors"
                    >
                      <CreditCard className="w-4 h-4" strokeWidth={1.5}/>
                      Begin the application
                    </Link>
                  </>
                ) : (
                  <button
                    type="button"
                    data-testid="cta-buy"
                    onClick={() => setShowBuy(true)}
                    className="w-full inline-flex items-center justify-center gap-3 bg-ink text-cream py-4 text-xs tracking-[0.25em] uppercase hover:bg-ink/80 transition-colors"
                  >
                    <CreditCard className="w-4 h-4" strokeWidth={1.5}/>
                    Get instant access
                  </button>
                )}

                <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-6 text-xs text-ink/60">
                  <div className="flex items-center gap-2"><Check className="w-3 h-3 text-ink"/>Instant download</div>
                  <div className="flex items-center gap-2"><Download className="w-3 h-3 text-ink"/>Opens on any device</div>
                  {!isFree && <div className="flex items-center gap-2"><Check className="w-3 h-3 text-ink"/>Lifetime access</div>}
                  {!isFree && <div className="flex items-center gap-2"><Check className="w-3 h-3 text-ink"/>Stripe-secured</div>}
                  {!isFree && <div className="flex items-center gap-2"><Heart className="w-3 h-3 text-ink"/>10% goes to charity</div>}
                </div>
                {!isFree ? (
                  <p className="mt-4 text-[10px] tracking-[0.22em] uppercase text-ink/50 text-center border-t border-ink/10 pt-3">
                    All sales final · No refunds, no exchanges
                  </p>
                ) : (
                  <p className="mt-4 text-[10px] tracking-[0.22em] uppercase text-ink/50 text-center border-t border-ink/10 pt-3">
                    Complimentary · Email gated
                  </p>
                )}
              </div>
            </Reveal>

            {/* What's inside */}
            {!isCoaching && (
              <Reveal delay={0.15}>
                <div className="mt-10">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-4">
                    What&apos;s inside
                  </p>
                  <ul className="space-y-3">
                    {(isFree
                      ? ["The full PDF — read on any device", "No fluff, no upsell"]
                      : product.bundle_size_mb > 5
                        ? ["The full PDF dossier", "Worksheets & checklists (where included)", "Companion audio (where included)", "Lifetime access to your copy"]
                        : ["The full PDF dossier", "Lifetime access to your copy"]
                    ).map((line, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-ink/80">
                        <Check className="w-4 h-4 mt-0.5 text-ink shrink-0" strokeWidth={1.5}/>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}

            {isCoaching && (
              <Reveal delay={0.15}>
                <div className="mt-10">
                  <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-4">
                    What you get on the call
                  </p>
                  <ul className="space-y-3">
                    {[
                      "90 minutes, one-on-one with Meghan",
                      "Pre-call brief reviewed before we meet",
                      "Strategy doc emailed within 24 hours",
                      "One follow-up async exchange",
                      "First-look at next available date upon checkout",
                    ].map((line, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm text-ink/80">
                        <Check className="w-4 h-4 mt-0.5 text-ink shrink-0" strokeWidth={1.5}/>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            )}
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      {!isFree && (
        <section className="py-10 border-b border-ink/10 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 sm:px-10 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            <TrustItem icon={ShieldCheck} title="Stripe-secured" sub="PCI-compliant checkout"/>
            <TrustItem icon={Download} title="Instant download" sub="One-tap secure delivery"/>
            <TrustItem icon={Check} title="Lifetime access" sub="Re-download anytime"/>
            <TrustItem icon={Heart} title="10% to charity" sub="Every paid order"/>
          </div>
        </section>
      )}

      <FreeGateModal
        open={showFreeGate}
        onClose={() => setShowFreeGate(false)}
        product={product}
      />
      <BuyModal
        open={showBuy}
        onClose={() => setShowBuy(false)}
        product={product}
      />
    </div>
  );
}

function TrustItem({ icon: Icon, title, sub }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <Icon className="w-5 h-5 text-ink" strokeWidth={1.5}/>
      <p className="text-xs tracking-[0.2em] uppercase text-ink">{title}</p>
      <p className="text-[10px] text-ink/50">{sub}</p>
    </div>
  );
}

// ──────────── FREE EMAIL GATE ────────────
function FreeGateModal({ open, onClose, product }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [brand, setBrand] = useState("");
  const [termsOk, setTermsOk] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    if (!open) { setName(""); setEmail(""); setBrand(""); setTermsOk(false); setDownloadUrl(null); }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!termsOk) { toast.error("Please tick the all-sales-final box to continue."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/studio/free-download`, {
        slug: product.slug, name, email, brand, consent_marketing: true, terms_accepted: true,
      });
      const url = `${API}${data.download_url}`;
      setDownloadUrl(url);
      toast.success("Your edition is in your library.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong.");
    } finally { setBusy(false); }
  };

  return (
    <Modal onClose={onClose}>
      <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-3">Free · Instant access</p>
      <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-3 text-ink">{product.title}</h2>
      <p className="text-ink/70 mb-8 font-serif italic">
        Drop your name and email — your edition lands in your private M Media library, ready to read in your browser.
      </p>
      {!downloadUrl ? (
        <form onSubmit={submit} className="space-y-3">
          <Input data-testid="free-name" placeholder="Your name" value={name} onChange={setName} required/>
          <Input data-testid="free-email" type="email" placeholder="Email" value={email} onChange={setEmail} required/>
          <Input data-testid="free-brand" placeholder="Brand or company (optional)" value={brand} onChange={setBrand}/>
          <TermsCheckbox checked={termsOk} onChange={setTermsOk} testId="free-terms"/>
          <button
            type="submit"
            data-testid="free-submit"
            disabled={busy || !termsOk}
            className="w-full bg-ink text-cream py-4 text-xs tracking-[0.25em] uppercase hover:bg-ink/80 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy ? "Opening…" : "Get instant access"}
          </button>
        </form>
      ) : (
        <div className="text-center py-8 border border-ink/15 bg-cream">
          <Check className="w-10 h-10 text-ink mx-auto mb-4" strokeWidth={1.2}/>
          <p className="font-display text-2xl text-ink mb-2">Your edition is waiting.</p>
          <p className="font-serif italic text-ink/65 mb-6 max-w-sm mx-auto text-sm">
            We&apos;ve sent your sign-in link to <strong className="font-medium text-ink">{email}</strong>. Read it instantly in your browser library — no download required.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <a
              href="/portal"
              data-testid="free-open-library"
              className="inline-flex items-center gap-2 bg-ink text-cream px-6 py-3 text-xs tracking-[0.25em] uppercase hover:bg-ink/80"
            >
              Open my library →
            </a>
            <a
              href={downloadUrl}
              data-testid="free-download-link"
              className="inline-flex items-center gap-2 border border-ink/40 text-ink px-6 py-3 text-xs tracking-[0.25em] uppercase hover:bg-ink hover:text-cream"
              target="_blank"
              rel="noreferrer"
            >
              <Download className="w-3.5 h-3.5" strokeWidth={1.5}/>
              Download a copy
            </a>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ──────────── PAID BUY MODAL → STRIPE ────────────
function BuyModal({ open, onClose, product }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [brand, setBrand] = useState("");
  const [notes, setNotes] = useState("");
  const [termsOk, setTermsOk] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setName(""); setEmail(""); setBrand(""); setNotes(""); setTermsOk(false); }
  }, [open]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!termsOk) { toast.error("Please tick the all-sales-final box to continue."); return; }
    setBusy(true);
    try {
      // Resilient POST: retries on 502/503/504/network blips (Cloudflare 520
      // sometimes hits this endpoint during peak load). User sees one click,
      // we handle up to 3 retries transparently. Long timeout because Stripe
      // can take a moment on the first call after cold start.
      const { data } = await postWithRetry(`${API}/api/studio/checkout`, {
        slug: product.slug,
        name, email, brand, notes,
        origin_url: window.location.origin,
        terms_accepted: true,
      }, { timeout: 18000 });
      if (data.url) {
        window.location.href = data.url;
      } else {
        toast.error("Couldn't start checkout.");
        setBusy(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Checkout failed. Try again.");
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      <p className="text-[10px] tracking-[0.3em] uppercase text-ink/60 mb-3">
        Checkout · Edition No. {product.edition_no}
      </p>
      <h2 className="font-display text-3xl sm:text-4xl leading-tight mb-2 text-ink">
        {product.title}
      </h2>
      <p className="font-display text-4xl text-ink mb-8">
        {formatPrice(product.price)}
      </p>
      <form onSubmit={submit} className="space-y-3">
        <Input data-testid="buy-name" placeholder="Full name" value={name} onChange={setName} required/>
        <Input data-testid="buy-email" type="email" placeholder="Email (for your receipt)" value={email} onChange={setEmail} required/>
        <Input data-testid="buy-brand" placeholder="Brand or company (optional)" value={brand} onChange={setBrand}/>
        {product.no_download && (
          <textarea
            data-testid="buy-notes"
            placeholder="Anything we should know before the call?"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full bg-cream border border-ink/15 px-4 py-3 text-sm focus:outline-none focus:border-ink/50 resize-none"
          />
        )}
        <TermsCheckbox checked={termsOk} onChange={setTermsOk} testId="buy-terms"/>
        <button
          type="submit"
          data-testid="buy-submit"
          disabled={busy || !termsOk}
          className="w-full bg-ink text-cream py-4 text-xs tracking-[0.25em] uppercase hover:bg-ink/80 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-3"
        >
          <Lock className="w-4 h-4" strokeWidth={1.5}/>
          {busy ? "Redirecting to Stripe…" : `Pay ${formatPrice(product.price)} securely`}
        </button>
        <p className="text-[10px] text-ink/40 text-center mt-4 tracking-wider">
          Powered by Stripe · PCI-compliant · We never see your card
        </p>
      </form>
    </Modal>
  );
}

function Modal({ children, onClose }) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-ink/80 backdrop-blur grid place-items-center p-4 sm:p-8 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          className="bg-cream w-full max-w-lg relative p-8 sm:p-12 my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            data-testid="modal-close"
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 grid place-items-center hover:bg-ink/5"
            aria-label="Close"
          >
            <X className="w-5 h-5 text-ink"/>
          </button>
          {children}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Input({ value, onChange, ...rest }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-cream border border-ink/15 px-4 py-3 text-sm focus:outline-none focus:border-ink/50"
      {...rest}
    />
  );
}

// Required acknowledgment — no refunds, all sales final.
function TermsCheckbox({ checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-3 mt-4 cursor-pointer select-none">
      <input
        type="checkbox"
        data-testid={testId}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 w-4 h-4 accent-ink shrink-0 cursor-pointer"
      />
      <span className="text-xs text-ink/70 leading-snug">
        I understand <strong className="text-ink">all sales are final</strong> — no refunds, no exchanges, no chargebacks. Digital goods, instantly delivered.
      </span>
    </label>
  );
}
